import { useEffect, useMemo, useRef, useState } from 'react'

import type { Transition } from 'framer-motion'
import { AnimatePresence } from 'framer-motion'

import confettiIcon from '@/assets/icons/cel.png'
import closeCircleIcon from '@/assets/icons/close-circle.png'
import { ENERGY_BADGE_ID } from '@/constants'
import { useGetJackpotWinner } from '@/query/winner/useGetJackpotWinner'

import {
  WINNER_TOAST_COLLISION_GAP_PX,
  WINNER_TOAST_ENTER_DELAY_MS,
  WINNER_TOAST_ENTER_DURATION_MS,
  WINNER_TOAST_VISIBLE_DURATION_MS,
} from './constants'
import { readDismissedRound, writeDismissedRound } from './storage'
import {
  CloseButton,
  ConfettiImage,
  Container,
  ContentGroup,
  LabelText,
  NameText,
  PrizeText,
  TextGroup,
} from './styles'

const OFFSCREEN_Y_PX = -200

const ENTER_TRANSITION: Transition = {
  y: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  opacity: { duration: 0.6, ease: 'easeOut' },
}

const EXIT_TRANSITION: Transition = {
  y: { duration: 1.8, ease: [0.4, 0, 0.2, 1] },
  opacity: { duration: 1.4, ease: 'easeIn' },
}

interface WinnerToastProps {
  /** coupon(메인) 스텝 여부 — 이탈 시 퇴장 애니메이션 없이 즉시 제거 */
  isActive: boolean
}

export default function WinnerToast({ isActive }: WinnerToastProps) {
  const { data: winner } = useGetJackpotWinner()
  // 이번 로드에서 끝났는지(자동·스크롤·스텝 이탈·X). X 로 닫은 회차는 storage 에 남아 다음 로드에도 미노출
  const [isFinished, setIsFinished] = useState(false)
  const [isShowing, setIsShowing] = useState(false)
  // localStorage 읽기는 회차가 바뀔 때만 (렌더마다 읽지 않게)
  const roundId = winner?.roundId
  const isDismissedRound = useMemo(() => (roundId ? readDismissedRound() === roundId : false), [roundId])
  const canShow = Boolean(winner) && !isDismissedRound && !isFinished
  const toastRef = useRef<HTMLDivElement>(null)

  // 메인 스텝 이탈 시 이번 로드에서는 재노출하지 않음 — 렌더 단계 setState 패턴
  if (!isActive && !isFinished) {
    setIsFinished(true)
    if (isShowing) setIsShowing(false)
  }

  // 진입 — 당첨자 데이터가 도착한 뒤 딜레이 두고 1회 노출
  useEffect(() => {
    if (!canShow) return
    const timer = setTimeout(() => setIsShowing(true), WINNER_TOAST_ENTER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [canShow])

  // 자동 종료 — 진입 애니메이션 완료 후 노출 시간 경과 시 퇴장
  useEffect(() => {
    if (!isShowing) return
    const timer = setTimeout(() => {
      setIsShowing(false)
      setIsFinished(true)
    }, WINNER_TOAST_ENTER_DURATION_MS + WINNER_TOAST_VISIBLE_DURATION_MS)
    return () => clearTimeout(timer)
  }, [isShowing])

  // 스크롤 종료 — 에너지 뱃지가 토스트 하단 + 30px 안으로 올라오면 퇴장 (#root 가 스크롤 컨테이너)
  useEffect(() => {
    if (!isShowing) return
    const scrollContainer = document.getElementById('root')
    if (!scrollContainer) return

    const handleScroll = () => {
      const target = document.getElementById(ENERGY_BADGE_ID)
      const toast = toastRef.current
      if (!target || !toast) return

      const isColliding =
        target.getBoundingClientRect().top <= toast.getBoundingClientRect().bottom + WINNER_TOAST_COLLISION_GAP_PX
      if (isColliding) {
        setIsShowing(false)
        setIsFinished(true)
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    // 진입 딜레이 동안 이미 충돌 지점까지 스크롤한 경우 즉시 퇴장
    handleScroll()
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [isShowing])

  const handleClose = () => {
    if (winner) writeDismissedRound(winner.roundId)
    setIsShowing(false)
    setIsFinished(true)
  }

  if (!isActive || !winner) return null

  return (
    <AnimatePresence>
      {isShowing && (
        <Container
          ref={toastRef}
          style={{ x: '-50%' }}
          initial={{ y: OFFSCREEN_Y_PX, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: OFFSCREEN_Y_PX, opacity: 0, transition: EXIT_TRANSITION }}
          transition={ENTER_TRANSITION}>
          <ContentGroup>
            <ConfettiImage src={confettiIcon} alt='' loading='eager' decoding='async' />
            <TextGroup>
              <LabelText>{winner.label}</LabelText>
              <NameText>{winner.winnerName}</NameText>
              <PrizeText>{winner.prizeName}</PrizeText>
            </TextGroup>
          </ContentGroup>
          <CloseButton type='button' aria-label='당첨자 안내 닫기' onClick={handleClose}>
            <img src={closeCircleIcon} alt='' loading='eager' decoding='async' />
          </CloseButton>
        </Container>
      )}
    </AnimatePresence>
  )
}
